/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Button,
  Link,
  NotificationKind,
  ToastNotification
} from 'carbon-components-react';
import React from 'react';
import { FormattedMessage, useIntl, MessageDescriptor } from 'react-intl';

import { Noop } from '../../../../ts';
import messages from './messages';

export interface NotificationProps {
  id: string;
  docUrl?: string;
  title?: string;
  message: string | React.ReactNode | MessageDescriptor;
  status: NotificationKind;
  transactionId?: string;
  values?: {};
  errorsCount?: number;
  removeAllErrors?: Noop;
  onClose?: (id: string) => void;
  onExpand?: Noop;
}

const Notification: React.FC<NotificationProps> = ({
  id,
  transactionId,
  status,
  title,
  docUrl,
  message,
  values,
  onClose,
  errorsCount,
  onExpand,
  removeAllErrors
}) => {
  const intl = useIntl();

  let kind = status as NotificationKind;
  if (!['error', 'success', 'warning'].includes(status)) {
    kind = 'info';
  }

  const renderMessage = () => {
    // React elements like an upgrade button
    if (React.isValidElement(message)) {
      return message;
    }

    // Messages that need to be translated with `react-intl`
    if (typeof message === 'object' && 'id' in message) {
      return <FormattedMessage {...message} values={values} />;
    }

    // String messages
    if (typeof message === 'string') {
      return message;
    }

    console.error(`${typeof message} is not a valid toast message`);
    return null;
  };

  const renderDocMessage = () => {
    if (!docUrl) {
      return null;
    }
    return (
      <FormattedMessage
        {...messages.reviewDoc}
        values={{
          a: chunk => (
            <Link href={docUrl} target="_blank" rel="noopener noreferrer">
              {chunk}
            </Link>
          )
        }}
      />
    );
  };

  // Display actions like `Remove` or `Expand`
  const renderMessageAction = () => {
    if (status !== 'error') {
      return null;
    }

    if (removeAllErrors) {
      return (
        <Button
          size="sm"
          kind="tertiary"
          className="bx--actionable-notification__action-button"
          onClick={removeAllErrors}>
          <FormattedMessage {...messages.dismiss} values={{ errorsCount }} />
        </Button>
      );
    }

    if (errorsCount > 1) {
      return (
        <Button
          size="sm"
          kind="tertiary"
          className="bx--actionable-notification__action-button"
          onClick={onExpand}>
          <FormattedMessage {...messages.viewErrors} values={{ errorsCount }} />
        </Button>
      );
    }

    return null;
  };

  const renderTransactionId = () => {
    if (transactionId && kind === 'error' && errorsCount <= 1) {
      return (
        <FormattedMessage
          {...messages.transactionId}
          values={{ id: transactionId }}
        />
      );
    }
    return null;
  };

  return (
    <ToastNotification
      className="ToastNotification"
      kind={kind}
      title={
        typeof title === 'string' ? title : intl.formatMessage(messages[kind])
      }
      subtitle={
        <>
          {renderMessage()}
          {renderMessageAction()}
          {renderDocMessage()}
        </>
      }
      caption={renderTransactionId()}
      onCloseButtonClick={() => onClose(id)}
    />
  );
};

Notification.defaultProps = {
  status: 'info',
  transactionId: undefined,
  values: {},
  errorsCount: 0,
  removeAllErrors: undefined,
  onExpand: undefined,
  onClose: () => {}
};

const NotificationComponent = React.memo(Notification);
NotificationComponent.displayName = 'Notification';

export default NotificationComponent;
